import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Label } from '../../../Components/UI/label';
import { Checkbox } from '../../../Components/UI/checkbox';
import { ArrowLeft, X, Check, Pi } from 'lucide-react';
import { getUsersArr } from '../../../Store/Reducers';
import CustomSearchableDropdown from '../../../Components/Common/CustomSearchableDD';
import { toast } from 'sonner';
import { setGlobalLoading } from '../../../Store/Actions';
import { useDispatch } from 'react-redux';
import { transferAssignmentAPI } from '../UsersAPI';
import { Input } from '../../../Components/UI/input';
import { sendRemindPINAPI } from '../../Settings/Profile/ProfileSectionAPI';

const TransferAssignments = ({ isOpen, onOpenChange, user, onSuccess }) => {
  const users = useSelector(getUsersArr);
  const agencyData = useSelector((state) => state.agencyData?.data);
  const [hasResent, setHasResent] = useState(false);

  // Single state object for all transfer options
  const [transferOptions, setTransferOptions] = useState({
    transferThreads: true,
    transferConnections: true,
    transferTasks: true
  });

  const [selectedUserID, setSelectedUserID] = useState('');
  const [PIN, setPIN] = useState('');
  const [acceptConsequences, setAcceptConsequences] = useState(false);
  const dispatch = useDispatch();

  // Filter out the current user from the dropdown options
  const userOptions = users
    ?.filter(u => u.userID !== user?.UserID)
    ?.map(u => ({
      value: u.userID,
      label: u.name,
    })) || [];

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleOpenChange = (open) => {
    onOpenChange(open);
  };

  const handleUserChange = (value) => {
    setSelectedUserID(value);
  };

  // Update transfer options using single state object
  const handleTransferOptionChange = (optionName, checked) => {
    setTransferOptions(prev => ({
      ...prev,
      [optionName]: checked
    }));
  };

  const getTransferChangeType = () => {
    if (transferOptions.transferThreads) return 'XFRTHR'
    else if (transferOptions.transferConnections) return 'XFRCONN'
    else if (transferOptions.transferTasks) return 'XFRTASK'
  }

  const handleResendPIN = async () => {
    if (hasResent) return;
    dispatch(setGlobalLoading(true));
    try {
      const response = await sendRemindPINAPI(agencyData?.Mobile, agencyData?.CC || '91');
      if (response?.error === false) {
        setHasResent(true);
        toast.success(response.message || 'PIN sent to your registered mobile number.');
      } else {
        toast.error(response?.message || 'Failed to send PIN.');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send PIN.');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleSubmit = async () => {
    // Validate transfer options using single state object
    const hasAnyTransferOption = Object.values(transferOptions).some(option => option);
    if (!hasAnyTransferOption) {
      toast.error('Please select at least one transfer option (Threads, Contacts, or Tasks).');
      return;
    }

    // Validate user selection
    if (!selectedUserID) {
      toast.error('Please select a executive to transfer the assignments.');
      return;
    }

    // Validation checkbox
    if (!acceptConsequences) {
      toast.error('Please accept the consequences to proceed with the transfer.');
      return;
    }

    if (PIN == '') {
      toast.error('Please enter a valid PIN.');
      return;
    }

    // API Data
    dispatch(setGlobalLoading(true));
    try {
      let data = {
        fromUserID: user?.UserID,
        toUserID: selectedUserID,
        pin: PIN,
        changeType: getTransferChangeType()
      };

      const response = await transferAssignmentAPI(data);
      if (response.error === false) {
        toast.success(response.message || 'Assignments transferred successfully.');
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(response.message || 'Failed to transfer assignments.');
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred during transfer.');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setTransferOptions({
        transferThreads: true,
        transferConnections: true,
        transferTasks: true
      });
      setSelectedUserID('');
      setPIN('');
      setAcceptConsequences(false);
      setHasResent(false);
    }
  }, [isOpen]);

  const handlePINChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input and max 4 characters
    if (/^\d{0,4}$/.test(value)) {
      setPIN(value);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="lg:!max-w-[400px]">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={handleClose}>
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Transfer Assignments</div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Transfer Assignments
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={handleClose}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Scrollable middle section */}
        <div className="lg:max-h-[70vh] max-h-[90vh] overflow-y-auto px-4">
          {/* Transfer Assignment Title and User Info */}
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              Transfer contacts, threads of <span className="font-bold">{user?.Name || 'Unknown User'}</span> to
            </div>

            {/* User Selection Field */}
            <div className="space-y-2">
              <CustomSearchableDropdown
                fieldID="toUser"
                required={true}
                label=""
                placeholder="Select Executive"
                options={userOptions}
                value={selectedUserID}
                onChange={handleUserChange}
                emptyMessage={<>No users available.</>}
                onSelect={handleUserChange}
                searchEnabled={true}
                disabled={false}
                className="w-full"
              />
            </div>

            {/* Acceptance Checkbox */}
            <div className="mb-1 flex items-start space-x-2">
              <Checkbox
                id="acceptConsequences"
                checked={acceptConsequences}
                onCheckedChange={setAcceptConsequences}
                className="mt-1"
              />
              <label htmlFor="acceptConsequences" className="cursor-pointer text-sm text-gray-700">
                I accept the consequences of these changes.
              </label>
            </div>

            {/* PIN Field */}
            <div className="md:grid md:grid-cols-2 space-y-2 gap-4">
              <div className=''>
                <Label htmlFor="pin" className="text-sm font-medium mb-1" required={true}>
                  PIN for Verification
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="pin"
                    type="password"
                    placeholder=""
                    value={PIN}
                    onChange={(e) => handlePINChange(e)}
                    className="w-full sahi-input"
                    style={{ width: 'calc(100% - 80px)' }}
                    autoComplete="off"
                    inputMode="numeric"
                    maxLength={4}
                  />
                  <div className="w-[80px] items-center">
                    {!hasResent && (
                      <Button
                        variant="secondary"
                        onClick={handleResendPIN}
                        disabled={false}
                        className="w-full whitespace-nowrap px-2"
                        type="button"
                      >
                        Remind
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with buttons */}
        <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-6 pt-0 border-t border-gray-200">
          <Button
            onClick={handleSubmit}
            className="w-full sm:w-auto"
          >
            <Check className="h-4 w-4" />
            Transfer
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            <X className="w-3 h-3 mr-1" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransferAssignments;